import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, filter, map, of, take, timeout } from 'rxjs';
import { Capability, ModuleAccessService } from '../services/module-access.service';

/**
 * Guard de rota por capacidade contratada. **Fail-closed**: espera os módulos do tenant
 * carregarem e só libera se a capacidade estiver ativa; módulo ausente, falha do endpoint
 * ou espera acima de 10 s redirecionam ao dashboard do tenant.
 *
 * Complementa o item de menu (`strictModules`) — o link some, e a URL digitada também não abre.
 * Não substitui a autorização do backend.
 */
export const moduleCapabilityGuard = (cap: Capability): CanActivateFn => (route) => {
  const access = inject(ModuleAccessService);
  const router = inject(Router);

  const tenantId = route.pathFromRoot
    .map(r => r.paramMap.get('tenantId'))
    .find((id): id is string => !!id) ?? null;

  const deny = () =>
    tenantId
      ? router.createUrlTree(['/', tenantId, 'dashboard'])
      : router.createUrlTree(['/auth/login']);

  if (tenantId) access.load(tenantId);

  return toObservable(access.loaded).pipe(
    filter(Boolean),
    take(1),
    timeout(10_000),
    map(() => (access.hasCapabilityStrict(cap) ? true : deny())),
    catchError(() => of(deny())),
  );
};
